// src/components/CategoryList.jsx
import React, { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";

const STORAGE_LANG_KEY = "site_lang";

/* ---------- Language messages ---------- */
const messages = {
  en: { noCategories: "No categories.", loadingLabel: "Loading categories" },
  fr: { noCategories: "Aucune catégorie.", loadingLabel: "Chargement des catégories" },
  ar: { noCategories: "لا توجد فئات.", loadingLabel: "جاري تحميل الفئات" },
  de: { noCategories: "Keine Kategorien.", loadingLabel: "Kategorien werden geladen" },
};

const detectLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_LANG_KEY);
    if (v) return v;
  } catch {}
  return (navigator.language || "en").split("-")[0];
};

/* ---------- Inject CSS once ---------- */
let cssLoaded = false;
function injectCSS() {
  if (cssLoaded) return;
  cssLoaded = true;

  const style = document.createElement("style");
  style.innerHTML = CATEGORY_CSS;
  document.head.appendChild(style);
}

const CategoryList = ({
  categories = [],
  loading = false,
  onCategoryClick,
  showCounts = true,
  className = "",
}) => {
  const lang = useMemo(() => detectLang(), []);
  const msg = messages[lang] || messages.en;

  useEffect(() => {
    injectCSS();
  }, []);

  /* ---------- Loading skeleton ---------- */
  if (loading) {
    return (
      <div className={`cat-card ${className}`} aria-busy="true">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="cat-skeleton" />
        ))}
      </div>
    );
  }

  if (!Array.isArray(categories) || categories.length === 0) {
    return <p className="cat-empty">{msg.noCategories}</p>;
  }

  return (
    <div className={`cat-card ${className}`}>
      <ul className="cat-list">

        {categories.map((cat) => {
          const key = cat.id ?? cat.slug ?? `${cat.name}-${Math.random()}`;
          const name = cat.name ?? cat.title ?? "—";
          const slug = cat.slug ?? cat.id;
          const count = typeof cat.count === "number" ? cat.count : null;

          return (
            <li key={key} className="cat-item">

              {typeof onCategoryClick === "function" ? (
                <button
                  className="cat-link"
                  onClick={(e) => {
                    e.preventDefault();
                    onCategoryClick(cat);
                  }}
                >
                  {name}
                </button>
              ) : (
                <NavLink
                  to={`/category/${encodeURIComponent(slug)}`}
                  className="cat-link"
                >
                  {name}
                </NavLink>
              )}

              {showCounts && count != null && (
                <span className="cat-count">{count}</span>
              )}

            </li>
          );
        })}

      </ul>
    </div>
  );
};

CategoryList.propTypes = {
  categories: PropTypes.array,
  loading: PropTypes.bool,
  onCategoryClick: PropTypes.func,
  showCounts: PropTypes.bool,
  className: PropTypes.string,
};

CategoryList.defaultProps = {
  categories: [],
  loading: false,
  onCategoryClick: undefined,
  showCounts: true,
  className: "",
};

export default CategoryList;


/* ---------- CSS ---------- */

const CATEGORY_CSS = `
.cat-card{
background:#fff;
border-radius:14px;
padding:16px;
border:1px solid rgba(253, 253, 253, 0.73);
box-shadow:0 6px 22px rgba(0,0,0,0.05);
transition:all .25s ease;
 --border:        rgba(0, 0, 0, 0.09);
}

.cat-list{
list-style:none;
margin:0;
padding:0;
display:flex;
flex-direction:column;
gap:8px;
 --border:        rgba(3, 3, 3, 0.56);
}

.cat-item{
display:flex;
align-items:center;
justify-content:space-between;
padding:8px 10px;
border-radius:8px;
transition:all .2s ease;
 --border:        rgba(0, 0, 0, 0.36);
}

.cat-item:hover{
background:#f6f7f9;
transform:translateX(4px);
}

.cat-link{
background:none;
border:none;
font-size:.92rem;
font-weight:600;
color:#111827;
text-decoration:none;
cursor:pointer;
font-family:Inter,system-ui;
}

.cat-link:hover{
color:#2563eb;
}

.cat-count{
font-size:.75rem;
font-weight:600;
background:#f1f3f7;
padding:3px 8px;
border-radius:20px;
color:#444;
}

.cat-empty{
color:#6b7280;
font-size:.9rem;
margin:0;
}

.cat-skeleton{
height:14px;
background:linear-gradient(90deg,#eee,#ddd,#eee);
background-size:200% 100%;
animation:catSk 1.2s infinite;
border-radius:6px;
}

@keyframes catSk{
0%{background-position:200% 0}
100%{background-position:-200% 0}
}

@media(max-width:768px){

.cat-card{
padding:12px;
}

.cat-link{
font-size:.88rem;
color:black;
}

}

@media (prefers-color-scheme: dark){

.cat-card{
background:white;
border-color:black;
}

.cat-item:hover{
background:rgba(71, 106, 107, 0.53);
}

.cat-link{
color:black;
}

.cat-count{
background:white;
color:black;
}

}
`;